import React, { useState, useEffect } from "react";
import { router } from "@inertiajs/react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

interface CrudDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  offerId: number;
  resourceType: "subject_lines" | "from_lines" | "footers" | "tracking_links" | "unsubscribe_links";
  item?: {
    id?: number;
    name?: string;
    description?: string;
    link?: string;
  } | null;
}

export default function CrudDialog({
  open,
  onOpenChange,
  title,
  description,
  offerId,
  resourceType,
  item = null,
}: CrudDialogProps) {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    link: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      if (item) {
        setFormData({
          name: item.name || "",
          description: item.description || "",
          link: item.link || "",
        });
      } else {
        setFormData({ name: "", description: "", link: "" });
      }
    }
  }, [item, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    let data;
    if (resourceType === "tracking_links" || resourceType === "unsubscribe_links") {
      data = { [resourceType.slice(0, -1)]: { name: formData.name, link: formData.link } };
    } else if (resourceType === "footers") {
      data = { [resourceType.slice(0, -1)]: { name: formData.name, description: formData.description } };
    } else {
      data = { [resourceType.slice(0, -1)]: { description: formData.description } };
    }

    if (item?.id) {
      // Update existing item
      router.patch(`/offers/${offerId}/${resourceType}/${item.id}`, data, {
        onFinish: () => {
          setLoading(false);
          onOpenChange(false);
        },
      });
    } else {
      // Create new item
      router.post(`/offers/${offerId}/${resourceType}`, data, {
        onFinish: () => {
          setLoading(false);
          onOpenChange(false);
        },
      });
    }
  };

  const getInputLabel = () => {
    switch (resourceType) {
      case "tracking_links":
      case "unsubscribe_links":
        return "Link";
      default:
        return "Description";
    }
  };

  const getInputValue = () => {
    return resourceType === "tracking_links" || resourceType === "unsubscribe_links"
      ? formData.link
      : formData.description;
  };

  const handleInputChange = (value: string) => {
    if (resourceType === "tracking_links" || resourceType === "unsubscribe_links") {
      setFormData({ ...formData, link: value });
    } else {
      setFormData({ ...formData, description: value });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            {(resourceType === "footers" ||
              resourceType === "tracking_links" ||
              resourceType === "unsubscribe_links") && (
              <div className="space-y-2">
                <label 
                  htmlFor="name-field" 
                  className="block text-sm font-medium text-gray-700 mb-2">
                  Name <span className="text-red-500">*</span>
                </label>
                <Input
                  id="name-field"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter name..."
                  required
                  disabled={loading}
                />
              </div>
            )}
            <div className="space-y-2">
              <label 
                htmlFor="input-field" 
                className="block text-sm font-medium text-gray-700 mb-2">
                {getInputLabel()} <span className="text-red-500">*</span>
              </label>
              <textarea
                id="input-field"
                value={getInputValue()}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder={`Enter ${getInputLabel().toLowerCase()}...`}
                rows={3}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                required
                disabled={loading}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-gradient-to-b from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 cursor-pointer"
            >
              {loading ? "Saving..." : item?.id ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}